import React from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaEye, FaEdit, FaTrash, FaSpinner } from 'react-icons/fa';
import { Button } from '@mui/material';
import styled from 'styled-components';

const ContentArea = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%);
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const ProductCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    border-color: rgba(74, 144, 226, 0.3);
  }
`;

const ProductImage = styled.div`
  height: 180px;
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  }
`;

const ProductInfo = styled.div`
  padding: 1.2rem;
`;

const ProductTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  color: white;
`;

const ProductPrice = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: #4a90e2;
  margin-bottom: 1rem;
`;

const ProductActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const EmptyState = styled(motion.div)`
  text-align: center;
  padding: 3rem 1rem;
  
  h3 {
    margin-bottom: 1rem;
    color: rgba(255, 255, 255, 0.8);
  }
  
  p {
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 2rem;
  }
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const ProductsTab = ({ products, loading, navigate, handleDeleteProduct }) => {
  return (
    <ContentArea
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h2 style={{ margin: 0, color: 'white' }}>Your Products</h2>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<FaPlus />}
          onClick={() => navigate('/marketplace/add-product')}
          style={{ 
            background: 'linear-gradient(90deg, #4a90e2, #63b3ed)',
            boxShadow: '0 4px 15px rgba(74, 144, 226, 0.3)',
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Add New Product
        </Button>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <FaSpinner style={{ 
            fontSize: '2rem', 
            color: '#4a90e2',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '1rem', color: 'rgba(255,255,255,0.7)' }}>
            Loading your products...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h3>No Products Yet</h3>
          <p>Start selling your digital products by adding your first item.</p>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<FaPlus />}
            onClick={() => navigate('/marketplace/add-product')}
            style={{ 
              background: 'linear-gradient(90deg, #4a90e2, #63b3ed)',
              boxShadow: '0 4px 15px rgba(74, 144, 226, 0.3)',
              textTransform: 'none',
              fontWeight: 600,
              padding: '0.5rem 1.5rem'
            }}
          >
            Create Your First Product
          </Button>
        </EmptyState>
      ) : (
        <ProductGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {products.map(product => (
            <ProductCard
              key={product.$id}
              variants={itemVariants}
            >
              <ProductImage 
                style={{ 
                  backgroundImage: `url(${product.imageUrl || 'https://via.placeholder.com/300x180?text=No+Image'})` 
                }}
              />
              <ProductInfo>
                <ProductTitle>{product.name}</ProductTitle>
                <ProductPrice>${product.price}</ProductPrice>
                <div style={{ 
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.9rem',
                  marginBottom: '0.5rem'
                }}>
                  {product.downloads || 0} downloads
                </div>
                <ProductActions>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FaEye />}
                    onClick={() => navigate(`/marketplace/product/${product.$id}`)}
                    style={{
                      borderColor: 'rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.8)',
                      textTransform: 'none'
                    }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FaEdit />}
                    onClick={() => navigate(`/marketplace/edit-product/${product.$id}`)}
                    style={{
                      borderColor: 'rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.8)',
                      textTransform: 'none'
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<FaTrash />}
                    onClick={() => handleDeleteProduct(product)}
                    style={{
                      borderColor: 'rgba(244,67,54,0.5)',
                      color: 'rgba(244,67,54,0.8)',
                      textTransform: 'none'
                    }}
                  >
                    Delete
                  </Button>
                </ProductActions>
              </ProductInfo>
            </ProductCard>
          ))}
        </ProductGrid>
      )}
    </ContentArea>
  );
};

export default ProductsTab;
